import { Link, NavLink, Outlet } from 'react-router-dom';
import { LogOut, FileText, LayoutDashboard, Users, Receipt, Hotel, Hand, Download, CreditCard, Banknote, KeyRound, Bell, CheckCircle2 } from 'lucide-react';
import { useState } from 'react';
import { AccountDialog } from './AccountDialog';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from './ui/button';
import { cn } from '@/lib/utils';
import type { SseStatus } from '@/hooks/useSse';
import { FeedbackNotes } from './FeedbackNotes';
import { RecentDebitsBell } from './RecentDebitsBell';
import { PaymentActionAlerts } from './PaymentActionAlerts';

interface Props {
  sseStatus: SseStatus;
}

export function Layout({ sseStatus }: Props) {
  const { user, signOut } = useAuth();
  const [accountOpen, setAccountOpen] = useState(false);
  if (!user) return null;

  const role = user.role;
  const isAdmin = role === 'ADMIN';
  const isAccountant = role === 'ACCOUNTANT';
  const isOperator = role === 'OPERATOR';
  const isVisaTeam = isOperator && user.team === 'VISA';
  const showAccounting = isAdmin || isAccountant;
  const showAdminOnly = isAdmin;

  const rolePillClass =
    role === 'ADMIN'
      ? 'role-pill role-pill-admin'
      : role === 'ACCOUNTANT'
        ? 'role-pill role-pill-acct'
        : 'role-pill role-pill-operator';

  return (
    <div className="flex min-h-screen">
      {/* `overflow-visible` is intentional and load-bearing: the bell's
          popover renders inside this aside, anchored to the bell button,
          and must extend outside the sidebar into the main area. Without
          overflow-visible the popover was getting clipped to the 240px
          sidebar width. */}
      <aside className="header-band sticky top-0 flex h-screen w-60 shrink-0 flex-col overflow-visible border-r border-white/5">
        <div className="flex items-center gap-2 px-5 py-5">
          <Link to="/" className="group flex items-center gap-3">
            <img
              src="/logo.png"
              alt="Trawish"
              className="h-9 w-9 rounded-lg shadow-md ring-1 ring-white/20 transition group-hover:ring-white/40"
            />
            <div className="flex flex-col leading-tight">
              <span className="text-base font-bold text-white">Trawish</span>
              <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-white/60">
                Payment ops
              </span>
            </div>
          </Link>
          {/* Bell sits in the top-left, immediately next to the brand.
              `relative z-50` so its popover stacks above the main area. */}
          <div className="relative z-50 ml-auto text-white/80">
            <RecentDebitsBell />
          </div>
        </div>

        <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-3 pb-3">
          <NavTab to="/" icon={<LayoutDashboard className="h-3.5 w-3.5" />}>Dashboard</NavTab>
          <NavTab to="/recent-debits" icon={<Receipt className="h-3.5 w-3.5" />}>Recent debits</NavTab>
          <NavTab to="/debits" icon={<Receipt className="h-3.5 w-3.5" />}>Debits (attribute)</NavTab>
          <NavTab to="/otp-alerts" icon={<Bell className="h-3.5 w-3.5" />}>OTP Alerts</NavTab>
          {isAdmin && (
            <NavTab to="/approvals" icon={<CheckCircle2 className="h-3.5 w-3.5" />}>Approvals</NavTab>
          )}
          {isAdmin && (
            <NavTab to="/sessions" icon={<KeyRound className="h-3.5 w-3.5" />}>Sessions</NavTab>
          )}
          {showAccounting && (
            <NavTab to="/transactions" icon={<FileText className="h-3.5 w-3.5" />}>Transactions</NavTab>
          )}
          {isAdmin && (
            <NavTab to="/clients" icon={<Users className="h-3.5 w-3.5" />}>Clients</NavTab>
          )}
          {showAccounting && (
            <NavTab to="/export" icon={<Download className="h-3.5 w-3.5" />}>Export</NavTab>
          )}
          {showAccounting && (
            <NavTab to="/cards" icon={<CreditCard className="h-3.5 w-3.5" />}>Cards</NavTab>
          )}
          {showAccounting && (
            <NavTab to="/holds" icon={<Hotel className="h-3.5 w-3.5" />}>Holds</NavTab>
          )}
          {!isVisaTeam && (
            <NavTab to="/cancel-queue" icon={<Hand className="h-3.5 w-3.5" />}>Cancel queue</NavTab>
          )}
          <NavTab to="/pay-requests" icon={<Banknote className="h-3.5 w-3.5" />}>Pay requests</NavTab>
        </nav>

        <div className="border-t border-white/5 px-3 py-3">
          {/* Footer is a single row: avatar block | grow | tiny status dot | sign-out.
              Long names truncate; nothing wraps onto a second line. */}
          <div className="flex items-center gap-2 px-1">
            <div className="flex min-w-0 flex-1 flex-col leading-tight">
              <span className="truncate text-xs font-semibold text-white">{user.name}</span>
              <div className="mt-1 flex items-center gap-1.5">
                <span className={rolePillClass}>{user.role}</span>
                {user.role === 'OPERATOR' && user.team ? (
                  <span className="truncate text-[10px] text-white/60">{user.team}</span>
                ) : null}
              </div>
            </div>
            {/* Status dot only — no text label down here; tooltip is enough. */}
            <span
              title={
                sseStatus === 'connected'
                  ? 'Real-time: active'
                  : sseStatus === 'connecting'
                    ? 'Connecting…'
                    : sseStatus === 'disconnected'
                      ? 'Disconnected'
                      : 'Idle'
              }
              className="shrink-0"
            >
              <ConnectionDotIcon status={sseStatus} />
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setAccountOpen(true)}
              aria-label="Account & password"
              title="Account & password"
              className="h-8 w-8 shrink-0 text-white/80 hover:bg-white/10 hover:text-white"
            >
              <KeyRound className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => void signOut()}
              aria-label="Sign out"
              className="h-8 w-8 shrink-0 text-white/80 hover:bg-white/10 hover:text-white"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      <main className="page-canvas flex-1 min-w-0">
        <div className="mx-auto max-w-7xl px-5 py-7">
          <Outlet />
        </div>
      </main>
      <PaymentActionAlerts />
      <FeedbackNotes />
      {accountOpen ? <AccountDialog onClose={() => setAccountOpen(false)} /> : null}
    </div>
  );
}

/** Dot-only realtime indicator for the sidebar footer. Full
 *  ConnectionDot is too wide for the narrow footer row; the tooltip
 *  on the wrapping span shows the textual status. */
function ConnectionDotIcon({ status }: { status: SseStatus }) {
  const color =
    status === 'connected'
      ? 'bg-emerald-400'
      : status === 'connecting'
        ? 'bg-amber-400'
        : 'bg-rose-500';
  return (
    <span className="relative flex h-2.5 w-2.5">
      {status === 'connected' && (
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
      )}
      <span className={cn('relative inline-flex h-2.5 w-2.5 rounded-full', color)} />
    </span>
  );
}

function NavTab({ to, icon, children }: { to: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        cn(
          'inline-flex h-9 items-center gap-2 rounded-md px-3 text-xs font-medium transition-all',
          isActive
            ? 'bg-white/15 text-white shadow-inner'
            : 'text-white/70 hover:bg-white/10 hover:text-white',
        )
      }
    >
      {icon}
      {children}
    </NavLink>
  );
}
